// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { JobStatus } from "src/pages/EmrConnections/JobRow/JobRow";
import {
  CalendarIcon,
  FileIcon,
  ReportIcon,
  WarningIcon,
} from "src/assets/icons";
import { WorklistTodayPanel } from "src/pages/EmrConnections/WorklistToday/WorklistToday";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import {
  Bullets,
  Callout,
  Chapter,
  GUIDE_NAMESPACE,
  GuideTable,
  Paragraph,
  Rich,
  SubHeading,
  Who,
  WhoRow,
} from "../GuideText/GuideText";
import { MockPage, mockCardBackground } from "../GuideMocks/PortalMocks";
import { MockConnectionsHeader } from "../GuideMocks/ConnectionMocks";
import {
  OutboxWalkthrough,
  ProblemsWalkthrough,
  ProcessedExamsWalkthrough,
} from "../Walkthroughs/DailyWalkthroughs";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

const PILL_COLORS: Record<JobStatus, { color: string; background: string }> = {
  Working: { color: "#0B3B30", background: "#7FD9C2" },
  Pending: { color: "#23253A", background: "#D5D7E0" },
  Failing: { color: "#4A0F0F", background: "#FFB4B4" },
  Off: { color: "inherit", background: "transparent" },
};

const JobPill = ({ status }: { status: JobStatus }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);

  return (
    <Typography
      component="span"
      fontSize="11px"
      fontWeight={700}
      sx={{
        color: status === "Off" ? mutedText : PILL_COLORS[status].color,
        backgroundColor: PILL_COLORS[status].background,
        border:
          status === "Off" ? `1px solid ${mutedText}` : "1px solid transparent",
        borderRadius: "999px",
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {t(status)}
    </Typography>
  );
};

const DirectionArrow = ({ direction }: { direction: "In" | "Out" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={direction === "In" ? ACCENT_TEAL : "#8A8DA2"}
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ verticalAlign: "-3px" }}
  >
    <path
      d={direction === "In" ? "M12 4v13M6 11l6 6 6-6" : "M12 20V7M6 13l6-6 6 6"}
    />
  </svg>
);

export const StatusChapter = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Chapter id="status" eyebrow="Day to day" title="Is it working?">
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <Paragraph text="Every job on the Connections page has a coloured label on the right:" />
      <GuideTable
        columns={["Label", "It means", "What to do"]}
        rows={[
          [
            <JobPill status="Working" />,
            "The job is running fine.",
            "Nothing.",
          ],
          [
            <JobPill status="Pending" />,
            "The hub is not installed yet, or has not tested this job yet.",
            "Finish installing the hub on the clinic PC, then wait a minute. If the hub PC is off, turn it on.",
          ],
          [
            <JobPill status="Failing" />,
            "The job cannot do its work.",
            "Read the red <b>Problems</b> box at the top of the page. It says what is wrong.",
          ],
          [
            <JobPill status="Off" />,
            "The job is switched off on purpose.",
            "Click <b>Turn on</b> if you want it.",
          ],
        ]}
      />
      <Typography fontSize="14px" lineHeight={1.65}>
        {t("The little arrow on each job shows the direction.")}{" "}
        {t("A teal arrow pointing down")} <DirectionArrow direction="In" />{" "}
        <Rich text="means things come <b>in</b> to RetinaLogik." />{" "}
        {t("A grey arrow pointing up")} <DirectionArrow direction="Out" />{" "}
        <Rich text="means reports go <b>out</b> to your clinic." />
      </Typography>
    </Chapter>
  );
};

export const WorklistTodayChapter = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Chapter
      id="worklist-today"
      eyebrow="Day to day"
      icon={<CalendarIcon width="16px" height="16px" />}
      title="Patients imported today"
    >
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <Paragraph text="Every worklist job shows what it did with today's appointments. The big number is how many <b>new</b> patients it imported: people on today's worklist who were not in RetinaLogik yet, so RetinaLogik created them." />
      <Box
        sx={{
          maxWidth: "380px",
          padding: "12px",
          borderRadius: "8px",
          backgroundColor: mockCardBackground(theme),
        }}
      >
        <WorklistTodayPanel
          counts={{ onWorklist: 10, new: 8, returning: 2, waiting: 0 }}
          dayLabel={t("Today")}
          lastReceivedLabel={t("Last received {{time}}", { time: "9:42" })}
        />
      </Box>
      <Bullets
        items={[
          "<b>New</b>: not in RetinaLogik before today's worklist. RetinaLogik created the patient.",
          "<b>Returning</b>: already in RetinaLogik, so nothing was created. Their exams still go to the right patient.",
          "<b>Waiting</b>: on the worklist, not in RetinaLogik yet. This usually clears within a minute.",
        ]}
      />
      <Paragraph text="<b>Last received</b> is the last time the hub sent appointments RetinaLogik had not seen yet. The numbers refresh by themselves every minute." />
      <Callout
        tone="tip"
        text="Patients without a birth date on the worklist are not counted. RetinaLogik cannot create them, so add the birth date in your EMR."
      />
    </Chapter>
  );
};

export const OutboxChapter = () => (
  <Chapter
    id="outbox"
    eyebrow="Day to day"
    icon={<ReportIcon width="16px" height="16px" />}
    title="Report Outbox"
  >
    <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
    <Paragraph text="Every report destination has a <b>Report Outbox</b>. It counts reports that are waiting (<b>Pending</b>), delivered (<b>Sent</b>) and stuck (<b>Failed</b>)." />
    <OutboxWalkthrough />
    <Paragraph text="Fix the cause first (the Problems box tells you what it is), then click <ui>Retry</ui>." />
  </Chapter>
);

export const ProcessedChapter = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();

  return (
    <Chapter
      id="processed"
      eyebrow="Day to day"
      title="Processed Exams: check the patient"
    >
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <Paragraph text="The <b>Processed Exams</b> tab lists every exam the hub brought in. Most exams match a patient on their own. When the hub is not sure, the exam says <b>Confirm Pending</b> and waits for you." />
      <ProcessedExamsWalkthrough />
      <Bullets
        items={[
          "<b>Keep Matched</b>: the suggested patient is right.",
          "<b>Create from Extracted</b>: this is a new patient. RetinaLogik creates them from the details in the file.",
          "<b>Choose a Different Patient</b>: search for the right patient, or click <b>Add Patient</b>, then click <b>Confirm</b>.",
        ]}
      />
      <Typography fontSize="14px" lineHeight={1.65}>
        {t("A red warning sign")}{" "}
        <Box
          component="span"
          color={theme.palette.mode === "dark" ? "#FF8080" : "#E10C0C"}
        >
          <WarningIcon
            width="14px"
            height="14px"
            style={{ verticalAlign: "-2px" }}
          />
        </Box>{" "}
        {t(
          "marks details that do not match the file. Check those carefully before you confirm."
        )}
      </Typography>
    </Chapter>
  );
};

const JOB_ROWS = [
  {
    status: "Complete",
    title: "Cirrus, room 2",
    files: 2,
    created: "2026-10-19 09:12",
    completed: "2026-10-19 09:13",
  },
  {
    status: "Open",
    title: "HFA, room 1",
    files: 1,
    created: "2026-10-19 09:40",
    completed: "N/A",
  },
];

const JobsTable = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { mutedText, dividerBorder } = getSurfaceTokens(theme);
  const columns = { xs: "1fr 1fr", sm: "110px minmax(0, 1.6fr) 60px 1fr 1fr" };

  return (
    <MockPage>
      <MockConnectionsHeader title={tg("Front desk PC")} />
      <Box
        sx={{
          backgroundColor: mockCardBackground(theme),
          borderRadius: "8px",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Box display="flex" alignItems="center" gap="8px" color={ACCENT_TEAL}>
          <FileIcon />
          <Typography fontSize="16px" fontWeight={600} color={ACCENT_TEAL}>
            {t("Jobs")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: "none", sm: "grid" },
            gridTemplateColumns: columns.sm,
            gap: "8px",
            padding: "9px 6px",
            fontSize: "12px",
            fontWeight: 700,
            color: mutedText,
            borderBottom: dividerBorder,
          }}
        >
          <span>{t("Status")}</span>
          <span>{t("Title")}</span>
          <span>{t("Files")}</span>
          <span>{t("Created")}</span>
          <span>{t("Completed")}</span>
        </Box>
        {JOB_ROWS.map((row) => {
          const color = row.status === "Complete" ? "#22BE39" : "#E2772E";

          return (
            <Box
              key={row.title}
              sx={{
                display: "grid",
                gridTemplateColumns: columns,
                gap: "8px",
                padding: "9px 6px",
                fontSize: "13px",
                alignItems: "center",
                borderBottom: dividerBorder,
              }}
            >
              <Box
                component="span"
                sx={{
                  justifySelf: "start",
                  fontSize: "12px",
                  fontWeight: 700,
                  color,
                  border: `1px solid ${color}`,
                  borderRadius: "4px",
                  padding: "0 5px",
                }}
              >
                {t(row.status)}
              </Box>
              <span>{tg(row.title)}</span>
              <span>{row.files}</span>
              <span>{row.created}</span>
              <span>{row.completed}</span>
            </Box>
          );
        })}
      </Box>
    </MockPage>
  );
};

export const JobsChapter = () => (
  <Chapter
    id="jobs"
    eyebrow="Day to day"
    icon={<FileIcon width="16px" height="16px" />}
    title="Jobs"
  >
    <Paragraph text="Click <ui>Jobs</ui> on the hub card to see each batch of files the hub sent and whether it finished." />
    <JobsTable />
    <Paragraph text="<b>Complete</b> (green) means everything in that batch arrived. <b>Failed</b> (red) means it did not; check the Problems box. Any other label (orange) means the batch is not finished yet." />
  </Chapter>
);

const you = <Who kind="you" text="You" />;
const it = (text = "IT") => <Who kind="it" text={text} />;

const WhoCell = ({ parts }: { parts: Array<JSX.Element | string> }) => (
  <Box display="flex" flexWrap="wrap" gap="4px 6px" alignItems="center">
    {parts.map((part, index) =>
      typeof part === "string" ? (
        <Typography key={index} component="span" fontSize="13.5px">
          <Rich text={part} />
        </Typography>
      ) : (
        <Box key={index} component="span">
          {part}
        </Box>
      )
    )}
  </Box>
);

export const ProblemsChapter = () => (
  <Chapter
    id="problems"
    eyebrow="Day to day"
    icon={<WarningIcon width="16px" height="16px" />}
    title="Problems and fixes"
  >
    <Paragraph text="When something needs fixing, the portal tells you in three places: an orange bar at the top of every page, the hub card, and a red <b>Problems</b> box on the Connections page. Problems clear by themselves once the cause is fixed. You never need to dismiss them." />
    <ProblemsWalkthrough />
    <SubHeading text="What each message means" />
    <Paragraph text="The words in <i>italics</i> are filled in with your own addresses, folders and names." />
    <GuideTable
      columns={["The portal says", "In plain words", "Who fixes it"]}
      rows={[
        [
          "<b>No contact since <i>date</i>. Check the hub PC is on and online.</b>",
          "The hub PC has been silent for over 10 minutes. It may be off, asleep, or without internet.",
          <WhoCell
            parts={[you, "turn the PC on, check the cable or Wi-Fi."]}
          />,
        ],
        [
          "<b>Can't reach <i>address</i>. Check it's on and the address is right.</b>",
          "The hub cannot find the PAC server or archive.",
          <WhoCell
            parts={[you, "check it is on.", it(), "checks the address."]}
          />,
        ],
        [
          "<b><i>address</i> didn't answer.</b>",
          "The server is there but not responding.",
          <WhoCell parts={[it()]} />,
        ],
        [
          "<b><i>address</i> refused the hub. Allow AE <i>EMRHUB</i> from the hub's IP.</b>",
          "The server does not let the hub in yet. The hub's name and address must be added on the server.",
          <WhoCell parts={[it("IT or archive vendor")]} />,
        ],
        [
          "<b><i>address</i> doesn't know the hub. Register AE <i>EMRHUB</i> ...</b>",
          "Same as above: the server has never heard of the hub.",
          <WhoCell parts={[it("IT or archive vendor")]} />,
        ],
        [
          "<b><i>address</i> dropped the connection / answered with an error</b>",
          "The server stopped halfway or said no to a request.",
          <WhoCell parts={[it("IT or archive vendor")]} />,
        ],
        [
          "<b><i>address</i> refused a report.</b>",
          "The archive would not accept a RetinaLogik report.",
          <WhoCell
            parts={[
              it("IT or archive vendor"),
              ", then <b>Retry</b> in the Report Outbox.",
            ]}
          />,
        ],
        [
          "<b>Folder <i>path</i> not found.</b>",
          "The folder does not exist, was renamed, or the network share is off.",
          <WhoCell
            parts={[
              you,
              "check the path in <b>Edit</b>.",
              it(),
              "checks the share.",
            ]}
          />,
        ],
        [
          "<b>No read access to <i>path</i> / No write access to <i>path</i></b>",
          "The folder exists, but the hub is not allowed in.",
          <WhoCell parts={[it(), "gives the hub's Windows account access."]} />,
        ],
        [
          "<b>Couldn't read / write to <i>path</i>. Check the share is online.</b>",
          "The shared folder is temporarily unavailable.",
          <WhoCell parts={[it()]} />,
        ],
        [
          "<b>Port <i>104</i> is already in use on the hub PC. Pick another port.</b>",
          "Another program on the hub PC already uses that door number.",
          <WhoCell
            parts={[
              you,
              "change <b>Hub port</b> in <b>Edit</b>, then update the machine too.",
            ]}
          />,
        ],
        [
          "<b>Windows blocked port <i>104</i> / Couldn't listen on port <i>104</i></b>",
          "The hub PC's firewall stops machines from reaching the hub.",
          <WhoCell parts={[it()]} />,
        ],
        [
          "<b>... is missing / is too long / is not in the right format. Fix them here; the connection is paused until then.</b>",
          "A setting is wrong. The connection waits until it is fixed.",
          <WhoCell parts={[you, "in <b>Edit</b>"]} />,
        ],
        [
          "<b>... is not supported by this hub version.</b>",
          "The hub is too old for this setting.",
          <WhoCell parts={[you, "see <toupdate>Update the hub</toupdate>"]} />,
        ],
        [
          "<b>Only <i>3</i> GB left on <i>C:</i>. Free up space or new exams stop.</b>",
          "The hub PC's disk is almost full.",
          <WhoCell parts={[it()]} />,
        ],
        [
          "<b>The hub PC's clock is <i>12</i> min ahead / behind.</b>",
          "The PC's time is wrong. This blocks safe sign-in.",
          <WhoCell
            parts={[you, "or", it(), ": Windows Settings, Time, Sync now."]}
          />,
        ],
        [
          "<b>Can't move processed files to <i>path</i>.</b>",
          "The hub cannot tidy finished files into the processed folder.",
          <WhoCell parts={[it(), "gives the hub's Windows account access."]} />,
        ],
        [
          "<b>Unexpected error. Check the hub PC's Event Log.</b>",
          "Something unusual happened.",
          <WhoCell parts={[it(), ", or contact RetinaLogik support."]} />,
        ],
      ]}
    />
    <SubHeading text="Messages when you save" />
    <GuideTable
      columns={["The portal says", "What to do"]}
      rows={[
        [
          "<b>Another enabled connection already reads from this source.</b>",
          "This folder or server is already set up. Edit the existing connection instead.",
        ],
        [
          "<b>An EMR connection already exists for this path.</b>",
          "Same folder used twice. Pick another folder or edit the existing one.",
        ],
        [
          "<b>This clinic already sends reports to the same host, port and AE title.</b>",
          "That destination already exists.",
        ],
        [
          "<b>Exams are brought in only for patients on a worklist. Switch on a worklist on this hub first.</b>",
          "Add or turn on a worklist, then try again.",
        ],
        [
          "<b>A PAC server can have one worklist, one exams and one reports job.</b>",
          "That job already exists on this server. Use <b>Edit</b> on its card.",
        ],
        [
          "<b>Only the hub's home clinic can do this.</b>",
          "Switch to the clinic that first added the hub.",
        ],
        [
          "<b>Remove the clinic's connections before taking it off the hub.</b>",
          "Delete that clinic's connections first, then remove the clinic.",
        ],
        [
          "<b>The installer isn't available yet. Please try again later.</b>",
          "Wait a few minutes and click download again.",
        ],
      ]}
    />
  </Chapter>
);
